import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect } from "react";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { PlusIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { RestrictionInfoBarStack } from "@whoosh/features/src/Restrictions/components/RestrictionInfoBarStack/RestrictionInfoBarStack";

import {
  beginRequestGroupAtom,
  removeRequestGroupAtom,
} from "../../../../atoms/actions/editRequestGroups/editRequestGroups";
import { setAllMembersToPlayingAtom } from "../../../../atoms/actions/setAllMembersToPlayingAtom/setAllMembersToPlayingAtom";
import { requestGroupControlsAtom } from "../../../../atoms/selectors/requestGroupControlsAtom/requestGroupControlsAtom";
import {
  currentBookingAtom,
  groupRestrictionsAtom,
} from "../../../../atoms/stores";
import { AddPlayerButton } from "../AddPlayerButton/AddPlayerButton";
import { EditableRound } from "./EditableRound/EditableRound";
import { EditableRoundsList_Booking$key } from "./__generated__/EditableRoundsList_Booking.graphql";

type Props = {
  bookingRef: EditableRoundsList_Booking$key;
  currentMembershipTypeId: string;
  isPrivate: boolean;
};

export const EditableRoundsList = ({
  bookingRef,
  currentMembershipTypeId,
  isPrivate,
}: Props) => {
  const dataBooking = useFragment(EditableRoundListFragmentString, bookingRef);
  const booking = useAtomValue(currentBookingAtom);
  const groupControls = useAtomValue(requestGroupControlsAtom);
  const beginRequestGroup = useSetAtom(beginRequestGroupAtom);
  const removeRequestGroup = useSetAtom(removeRequestGroupAtom);
  const rounds = booking?.rounds;
  const sortedRounds = [...(rounds ?? [])].sort((a, b) => a.index - b.index);
  const isAggregateRequest = sortedRounds.some(
    (round) => round.bookingIndex != null
  );
  const requestGroupCount = isAggregateRequest
    ? Math.max(...sortedRounds.map((round) => round.bookingIndex ?? 0)) + 1
    : 1;
  const roundIdList = sortedRounds.map((round) => round.localId);
  const groupRestrictions = useAtomValue(groupRestrictionsAtom);
  const setAllMembersToPlaying = useSetAtom(setAllMembersToPlayingAtom);

  const hasRounds = rounds?.length && rounds?.length > 0;
  // We need to check if all remaining rounds are members or member holds to set all members to playing (because we don't want to leave a member hold as the non-playing host)
  const isAllMembers =
    hasRounds &&
    rounds?.every((round) => {
      const clientType = round.player.clientType;
      return clientType === "member" || clientType === "memberHold";
    });
  useEffect(() => {
    if (isAllMembers) {
      setAllMembersToPlaying();
    }
  }, [isAllMembers, setAllMembersToPlaying]);

  return (
    <VStack
      gap={2}
      backgroundColor="surface-secondary"
      paddingBottom={2}
      testID="editable-rounds-list"
    >
      <Box paddingHorizontal={5}>
        <RestrictionInfoBarStack
          restrictions={groupRestrictions ?? []}
          restrictionContext="Patron"
        />
      </Box>
      {isAggregateRequest
        ? Array.from({ length: requestGroupCount }, (_, groupIndex) => (
            <VStack
              key={groupIndex}
              gap={1}
              testID={`editable-request-group-${groupIndex}`}
            >
              <HStack
                paddingHorizontal={5}
                paddingVertical={3}
                alignItems="center"
                justifyContent="space-between"
              >
                <Text variant="mainBaseSemiBold">
                  {t`Group ${groupIndex + 1}`}
                </Text>
                {groupControls.canRemove ? (
                  <Button
                    variant="ghost"
                    accessibilityLabel={t`Remove group ${groupIndex + 1}`}
                    testID={`remove-request-group-${groupIndex}`}
                    onPress={() => removeRequestGroup(groupIndex)}
                  >
                    {t`Remove`}
                  </Button>
                ) : null}
              </HStack>
              {sortedRounds
                .filter((round) => (round.bookingIndex ?? 0) === groupIndex)
                .map((round) => (
                  <EditableRound
                    key={round.localId}
                    roundId={round.localId}
                    bookingRef={dataBooking}
                    currentMembershipTypeId={currentMembershipTypeId}
                  />
                ))}
              <AddPlayerButton
                isPrivate={isPrivate}
                bookingIndex={groupIndex}
              />
            </VStack>
          ))
        : roundIdList.map((roundId) => (
            <EditableRound
              key={roundId}
              roundId={roundId}
              bookingRef={dataBooking}
              currentMembershipTypeId={currentMembershipTypeId}
            />
          ))}
      {!isAggregateRequest ? <AddPlayerButton isPrivate={isPrivate} /> : null}
      {groupControls.canAdd ? (
        <Box paddingHorizontal={5} paddingVertical={3}>
          <Button
            variant="outline"
            leftIcon={<PlusIcon />}
            accessibilityLabel={t`Add Group`}
            testID="add-request-group"
            onPress={() => beginRequestGroup()}
          >
            {t`Add Group`}
          </Button>
        </Box>
      ) : null}
    </VStack>
  );
};
const EditableRoundListFragmentString = graphql`
  fragment EditableRoundsList_Booking on Booking {
    ...EditableRound_Booking
  }
`;
